import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Ticket, Crown, Clock, Users } from "lucide-react";
import Countdown from "./Countdown";
import { api } from "../lib/api";

const BUYERS_THRESHOLD = 20;

/**
 * Bandeau permanent visible sur la page d'accueil tant que ``is_before_launch``
 * est vrai. Affiche : titre, sous-titre, countdown vers le 1er septembre 2026,
 * encart offre pré-ouverture si actif, ligne preuve sociale (compteur public
 * de joueurs prêts) et 2 CTA (Acheter / Mes codes).
 *
 * Props:
 *   - config: payload retourné par GET /api/launch/config
 */
export default function PrelaunchBanner({ config }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!config?.is_before_launch) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/public/prelaunch-stats");
        if (!cancelled) setStats(data);
      } catch (e) {
        // silent — strictly cosmetic
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [config]);

  if (!config || !config.launch_message_enabled || !config.is_before_launch) return null;
  const buyersCount = Number(stats?.buyers_count || 0);
  const showCount = buyersCount >= BUYERS_THRESHOLD;
  return (
    <div
      data-testid="prelaunch-banner"
      className="rounded-3xl border border-amber-400/40 bg-gradient-to-br from-amber-400/12 via-fuchsia-500/8 to-cyan-500/5 p-5 sm:p-7 mb-10 relative overflow-hidden"
    >
      <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-amber-400/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-20 w-56 h-56 rounded-full bg-fuchsia-500/10 blur-3xl pointer-events-none" />
      <div className="relative grid md:grid-cols-12 gap-5 sm:gap-7 items-center">
        <div className="md:col-span-7 space-y-2.5 min-w-0">
          <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-amber-400/45 bg-amber-400/10 text-amber-200 text-[10px] uppercase tracking-[0.3em] font-mono-stat">
            <Sparkles className="w-3 h-3" />
            Pré-ouverture
          </span>
          <h2
            data-testid="prelaunch-banner-title"
            className="font-display font-black uppercase tracking-tighter text-2xl sm:text-3xl leading-tight"
          >
            Lancement officiel le{" "}
            <span className="text-amber-300">1er septembre 2026</span>
          </h2>
          <p className="text-sm sm:text-base text-white/70 leading-relaxed">
            Les premiers quiz arrivent bientôt. Tu peux déjà acheter tes codes et
            les garder dans ton espace «&nbsp;Mes codes&nbsp;».
          </p>
          <div className="flex items-center gap-2 pt-1">
            <Clock className="w-4 h-4 text-cyan-300 shrink-0" />
            <span
              data-testid="prelaunch-banner-countdown"
              className="text-sm font-mono-stat text-cyan-100"
            >
              <Countdown
                target={config.launch_date}
                kind="opening"
                dataTestid="prelaunch-banner-countdown-value"
              />
            </span>
          </div>
          {stats && (
            <div
              data-testid="prelaunch-banner-social"
              className="flex items-start gap-2 pt-1.5"
            >
              <Users className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" />
              <div className="text-xs sm:text-sm">
                {showCount ? (
                  <>
                    <div
                      data-testid="prelaunch-banner-buyers-count"
                      className="font-display font-bold text-emerald-200"
                    >
                      Déjà{" "}
                      <span data-testid="prelaunch-buyers-number">
                        {buyersCount}
                      </span>{" "}
                      joueurs prêts pour le lancement
                    </div>
                    <div className="text-white/55 mt-0.5">
                      Ils ont déjà leurs codes pour participer aux premiers quiz du 1er septembre 2026.
                    </div>
                  </>
                ) : (
                  <div
                    data-testid="prelaunch-banner-buyers-generic"
                    className="text-emerald-200"
                  >
                    Les premiers joueurs réservent déjà leurs codes pour septembre.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="md:col-span-5 space-y-3">
          {config.is_offer_active && (
            <div
              data-testid="prelaunch-banner-offer"
              className="rounded-2xl border border-fuchsia-400/45 bg-gradient-to-br from-fuchsia-500/10 to-amber-400/10 p-4"
            >
              <div className="flex items-center gap-2 mb-1.5">
                <Crown className="w-4 h-4 text-amber-300" />
                <span className="text-[10px] uppercase tracking-[0.3em] font-mono-stat text-amber-200">
                  Offre limitée
                </span>
              </div>
              <div className="font-display font-bold text-sm sm:text-base">
                Pack Découverte boosté
              </div>
              <p className="text-xs sm:text-sm text-white/70 mt-0.5">
                <span className="text-amber-200 font-semibold">
                  7 codes standards pour 5 €
                </span>{" "}
                jusqu&apos;au 31 août 2026.
              </p>
            </div>
          )}
          <div className="flex gap-2">
            <button
              data-testid="prelaunch-banner-cta-buy"
              onClick={() => navigate("/acheter")}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-amber-400 hover:bg-amber-300 text-black font-display font-bold uppercase tracking-wider text-xs sm:text-sm active:scale-95 transition-all glow-primary"
            >
              <Sparkles className="w-4 h-4" />
              Acheter des codes
            </button>
            <button
              data-testid="prelaunch-banner-cta-mycodes"
              onClick={() => navigate("/mes-codes")}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/8 hover:bg-white/12 border border-white/15 text-white font-display font-bold uppercase tracking-wider text-xs sm:text-sm active:scale-95 transition-all"
            >
              <Ticket className="w-4 h-4" />
              Mes codes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
